import React, { memo, useCallback, useContext } from 'react'

import usePrivateModeAccess from 'Pods/ThreeDStudio/Hooks/usePrivateModeAccess'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import PaidFeatureBadge from 'Pods/ThreeDStudio/Shared/PaidFeatureBadge/PaidFeatureBadge'
import ToggleSelector from 'Pods/ThreeDStudio/Shared/ToggleSelector/ToggleSelector'
import { setIsPrivate } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { openPaidFeatureModal, openSignInModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

function PrivateModeSelector() {
  const t = useTranslation()
  const hasPrivateModeAccess = usePrivateModeAccess()
  const { userLoggedIn } = useContext(ThreeDStudioPageContext)

  // This selector is SSR-rendered on a fast page, so the paid variant must derive
  // from the access tier alone. The tier is null in the shared SSR store and only
  // hydrates from /load-services.js after mount, so it is null both server-side and
  // on the client's first (hydration) render - keeping the tree identical across
  // that boundary. Gating on isLoggedIn (pageConfig) instead would swap the
  // click handler on the first client render once pageConfig resolves, tripping a
  // React 18 hydration mismatch. Subscribed/credit users briefly see the crown and
  // it collapses once their tier resolves - the accepted per-user cost.
  const showPaidVariant = !hasPrivateModeAccess

  // Logged-out users get the sign-in modal first (they can't subscribe without an
  // account and their subscription price is not hydrated); logged-in users without
  // access get the paid-feature upsell. userLoggedIn comes from the studio page
  // context (the same source the sign-in modal's render guard uses), not a bare
  // usePageConfig, whose default channel omits isCurrentUser on this fast page.
  const handlePaidClick = useCallback(() => {
    if (!userLoggedIn) {
      openSignInModal('signup')
      return
    }
    openPaidFeatureModal()
  }, [userLoggedIn])

  // Paid variant keeps the toggle interactive (not disabled): clicking it opens the
  // subscription modal instead of flipping isPrivate, so the switch stays visually
  // off while the user sees the upsell.
  return (
    <ToggleSelector
      id="private-mode-selector"
      keyName="isPrivate"
      sliceName="modelSettings"
      label={t('shared.privateMode.label')}
      setFunction={showPaidVariant ? handlePaidClick : setIsPrivate}
      tooltipMessage={t('shared.privateMode.tooltip')}
      badge={showPaidVariant ? <PaidFeatureBadge label={t('shared.subscribersOnly.title')} /> : null}
    />
  )
}

export default memo(PrivateModeSelector)
